import React, { useState, useEffect, useMemo } from "react";
import {
  Search,
  Download,
  Eye,
  FileText,
  ShoppingBag,
  Store,
  Building2,
  Phone,
  MapPin,
  ChevronRight,
  ArrowLeft,
  X,
  Truck,
  Printer,
  RefreshCw,
  User,
  Hash,
  CreditCard
} from "lucide-react";

const BASE_URL = "https://api.visiontrackcrm.com";

// Helper date formatters
const formatDateToDDMMYYYY = (dateVal) => {
  if (!dateVal) return "-";
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return String(dateVal).split("T")[0];
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (e) {
    return String(dateVal).split("T")[0];
  }
};

const getDateOnlyStr = (dateVal) => {
  if (!dateVal) return "";
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return String(dateVal).split("T")[0].split(" ")[0];
    return d.toISOString().split("T")[0];
  } catch (e) {
    return "";
  }
};

// Helper to normalize Optical Sales Order Status strictly to 3 states: OPEN, CLOSED, DELIVERED
const getNormalizedOpticalStatus = (order) => {
  if (!order) return "OPEN";
  const st = String(order.status || "").toLowerCase().trim();
  if (st === "delivered") return "DELIVERED";
  if (st === "closed" || st === "close" || st === "completed") return "CLOSED";
  if (
    st === "not opened" ||
    st === "notopened" ||
    st === "not_opened" ||
    st === "open" ||
    st === "pending"
  ) {
    return "OPEN";
  }

  // Calculate effective order amount
  const directTotal = Number(order.total_amount ?? order.totalAmount ?? order.net_amount ?? order.amount ?? order.total ?? 0);
  const adv = Number(order.advance_paid ?? order.advance_amount ?? order.paid_amount ?? order.paid ?? 0);
  const bal = Number(order.balance_amount ?? order.balance_due ?? order.due_amount ?? 0);
  const total = directTotal > 0 ? directTotal : (adv + bal);

  // If order amount is 0 or eye test lead without items, it's OPEN
  if (total === 0) {
    return "OPEN";
  }

  // Default to CLOSED if total > 0 and not delivered
  return "CLOSED";
};

// CSV Downloader Helper
const downloadCSV = (filename, data, headers) => {
  if (!data || !data.length) {
    alert("No data available to export.");
    return;
  }
  const csvRows = [];
  csvRows.push(headers.map((h) => `"${h.label}"`).join(","));

  for (const row of data) {
    const values = headers.map((h) => {
      let val = h.accessor(row);
      if (val === null || val === undefined) val = "";
      const escaped = String(val).replace(/"/g, '""');
      return `"${escaped}"`;
    });
    csvRows.push(values.join(","));
  }

  const csvString = csvRows.join("\n");
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default function StoreDirectory() {
  // Navigation State
  // mainTab: 'wholesalers' | 'retail_stores'
  const [mainTab, setMainTab] = useState("wholesalers");

  // Wholesalers State
  const [wholesalers, setWholesalers] = useState([]);
  const [wholesalersLoading, setWholesalersLoading] = useState(false);
  const [wholesalerSearch, setWholesalerSearch] = useState("");
  const [wholesalerStatusFilter, setWholesalerStatusFilter] = useState("ALL");
  const [selectedWholesaler, setSelectedWholesaler] = useState(null);

  // Wholesaler Orders State
  const [wholesalerOrders, setWholesalerOrders] = useState([]);
  const [wholesalerOrdersLoading, setWholesalerOrdersLoading] = useState(false);
  const [wholesalerOrderSearch, setWholesalerOrderSearch] = useState("");
  const [retailStoreFilter, setRetailStoreFilter] = useState("ALL");
  const [wholesalerDateFilter, setWholesalerDateFilter] = useState("ALL");
  const [wholesalerFromDate, setWholesalerFromDate] = useState("");
  const [wholesalerToDate, setWholesalerToDate] = useState("");
  const [wholesalerStatusOrderFilter, setWholesalerStatusOrderFilter] = useState("ALL");
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);

  // Retail Stores State
  const [retailStores, setRetailStores] = useState([]);
  const [retailStoresLoading, setRetailStoresLoading] = useState(false);
  const [retailSearch, setRetailSearch] = useState("");
  const [retailCityFilter, setRetailCityFilter] = useState("ALL");
  const [retailStatusFilter, setRetailStatusFilter] = useState("ALL");
  const [selectedRetailStore, setSelectedRetailStore] = useState(null);

  // Selected Retail Store Sub-tabs: 'eyetests' | 'optical_sales' | 'ecommerce_orders'
  const [retailSubTab, setRetailSubTab] = useState("eyetests");

  // Retail Store Eye Tests State
  const [eyeTests, setEyeTests] = useState([]);
  const [eyeTestsLoading, setEyeTestsLoading] = useState(false);
  const [eyeTestSearch, setEyeTestSearch] = useState("");
  const [eyeTestDateFilter, setEyeTestDateFilter] = useState("ALL");
  const [eyeTestFromDate, setEyeTestFromDate] = useState("");
  const [eyeTestToDate, setEyeTestToDate] = useState("");
  const [selectedEyeTestModal, setSelectedEyeTestModal] = useState(null);

  // Retail Store Optical Sales State
  const [opticalSales, setOpticalSales] = useState([]);
  const [opticalSalesLoading, setOpticalSalesLoading] = useState(false);
  const [opticalSalesSearch, setOpticalSalesSearch] = useState("");
  const [opticalSalesStatusFilter, setOpticalSalesStatusFilter] = useState("ALL");
  const [opticalSalesPaymentFilter, setOpticalSalesPaymentFilter] = useState("ALL");
  const [opticalSalesDateFilter, setOpticalSalesDateFilter] = useState("ALL");
  const [opticalSalesFromDate, setOpticalSalesFromDate] = useState("");
  const [opticalSalesToDate, setOpticalSalesToDate] = useState("");
  const [selectedBillModal, setSelectedBillModal] = useState(null);

  // Retail Store Placed E-Commerce Orders State
  const [retailEcommOrders, setRetailEcommOrders] = useState([]);
  const [retailEcommLoading, setRetailEcommLoading] = useState(false);
  const [retailEcommSearch, setRetailEcommSearch] = useState("");
  const [retailEcommStatusFilter, setRetailEcommStatusFilter] = useState("ALL");
  const [retailEcommDateFilter, setRetailEcommDateFilter] = useState("ALL");
  const [retailEcommFromDate, setRetailEcommFromDate] = useState("");
  const [retailEcommToDate, setRetailEcommToDate] = useState("");

  // Initial Load
  useEffect(() => {
    fetchWholesalers();
    fetchRetailStores();
  }, []);

  // Fetch Wholesalers
  const fetchWholesalers = async () => {
    try {
      setWholesalersLoading(true);
      const res = await fetch(`${BASE_URL}/ecommerce/users`);
      const data = await res.json();
      if (data?.success && Array.isArray(data.data)) {
        setWholesalers(data.data);
      } else {
        const fbRes = await fetch(`${BASE_URL}/ecommerce/stores`);
        const fbData = await fbRes.json();
        if (fbData?.success && Array.isArray(fbData.data)) {
          setWholesalers(fbData.data);
        } else {
          setWholesalers([]);
        }
      }
    } catch (e) {
      console.error("Error fetching wholesalers:", e);
      setWholesalers([]);
    } finally {
      setWholesalersLoading(false);
    }
  };

  // Fetch Retail Stores
  const fetchRetailStores = async () => {
    try {
      setRetailStoresLoading(true);
      const res = await fetch(`${BASE_URL}/registration/stores`);
      const data = await res.json();
      if (data?.success && Array.isArray(data.data)) {
        setRetailStores(data.data);
      } else {
        setRetailStores([]);
      }
    } catch (e) {
      console.error("Error fetching retail stores:", e);
      setRetailStores([]);
    } finally {
      setRetailStoresLoading(false);
    }
  };

  // Load Wholesaler Orders
  const loadWholesalerOrders = async (storeCode) => {
    if (!storeCode) return;
    try {
      setWholesalerOrdersLoading(true);
      const code = String(storeCode).trim().toUpperCase();
      const res = await fetch(`${BASE_URL}/orders/store-orders/${encodeURIComponent(code)}`);
      const data = await res.json();
      if (data?.success && Array.isArray(data.data)) {
        setWholesalerOrders(data.data);
      } else {
        setWholesalerOrders([]);
      }
    } catch (e) {
      console.error("Error fetching wholesaler orders:", e);
      setWholesalerOrders([]);
    } finally {
      setWholesalerOrdersLoading(false);
    }
  };

  // Load Retail Store Eye Tests
  const loadRetailEyeTests = async (storeCode) => {
    if (!storeCode) return;
    try {
      setEyeTestsLoading(true);
      const code = String(storeCode).trim().toUpperCase();
      const res = await fetch(`${BASE_URL}/eyeexam?storeCode=${encodeURIComponent(code)}`);
      const data = await res.json();
      if (data?.success && Array.isArray(data.exams)) {
        setEyeTests(data.exams);
      } else {
        setEyeTests([]);
      }
    } catch (e) {
      console.error("Error fetching eye tests:", e);
      setEyeTests([]);
    } finally {
      setEyeTestsLoading(false);
    }
  };

  // Load Retail Store Optical Sales
  const loadRetailOpticalSales = async (storeCode) => {
    if (!storeCode) return;
    try {
      setOpticalSalesLoading(true);
      const code = String(storeCode).trim().toUpperCase();
      const res = await fetch(`${BASE_URL}/opticalorders?storeCode=${encodeURIComponent(code)}`);
      const data = await res.json();
      if (data?.success && Array.isArray(data.orders)) {
        setOpticalSales(data.orders);
      } else {
        setOpticalSales([]);
      }
    } catch (e) {
      console.error("Error fetching optical orders:", e);
      setOpticalSales([]);
    } finally {
      setOpticalSalesLoading(false);
    }
  };

  // Load Retail Store Placed E-Commerce Orders
  const loadRetailEcommerceOrders = async (storeCode) => {
    if (!storeCode) return;
    try {
      setRetailEcommLoading(true);
      const code = String(storeCode).trim().toUpperCase();
      const res = await fetch(`${BASE_URL}/orders/my-orders/${encodeURIComponent(code)}`);
      const data = await res.json();
      if (data?.success && Array.isArray(data.data)) {
        setRetailEcommOrders(data.data);
      } else {
        setRetailEcommOrders([]);
      }
    } catch (e) {
      console.error("Error fetching retail ecommerce orders:", e);
      setRetailEcommOrders([]);
    } finally {
      setRetailEcommLoading(false);
    }
  };

  // Handle Clicking a Wholesaler
  const handleSelectWholesaler = (w) => {
    setSelectedWholesaler(w);
    setWholesalerOrderSearch("");
    setRetailStoreFilter("ALL");
    setWholesalerDateFilter("ALL");
    setWholesalerFromDate("");
    setWholesalerToDate("");
    setWholesalerStatusOrderFilter("ALL");
    loadWholesalerOrders(w.store_code);
  };

  // Handle Clicking a Retail Store
  const handleSelectRetailStore = (s) => {
    setSelectedRetailStore(s);
    setRetailSubTab("eyetests");
    // Reset individual filters
    setEyeTestSearch("");
    setEyeTestDateFilter("ALL");
    setEyeTestFromDate("");
    setEyeTestToDate("");

    setOpticalSalesSearch("");
    setOpticalSalesStatusFilter("ALL");
    setOpticalSalesPaymentFilter("ALL");
    setOpticalSalesDateFilter("ALL");
    setOpticalSalesFromDate("");
    setOpticalSalesToDate("");

    setRetailEcommSearch("");
    setRetailEcommStatusFilter("ALL");
    setRetailEcommDateFilter("ALL");
    setRetailEcommFromDate("");
    setRetailEcommToDate("");

    // Load initial data
    loadRetailEyeTests(s.store_code);
    loadRetailOpticalSales(s.store_code);
    loadRetailEcommerceOrders(s.store_code);
  };

  // Helper date range check
  const checkDateInRange = (dateVal, filterType, fromDate, toDate) => {
    if (filterType === "ALL") return true;
    const dStr = getDateOnlyStr(dateVal);
    if (!dStr) return false;
    const todayStr = new Date().toISOString().split("T")[0];

    if (filterType === "TODAY") {
      return dStr === todayStr;
    }
    if (filterType === "WEEK") {
      const current = new Date();
      const day = current.getDay();
      const diff = day === 0 ? 6 : day - 1;
      const firstDay = new Date(current);
      firstDay.setDate(current.getDate() - diff);
      const firstStr = firstDay.toISOString().split("T")[0];
      return dStr >= firstStr && dStr <= todayStr;
    }
    if (filterType === "MONTH") {
      const monthPrefix = todayStr.substring(0, 7);
      return dStr.startsWith(monthPrefix);
    }
    if (filterType === "RANGE" || fromDate || toDate) {
      if (fromDate && toDate) return dStr >= fromDate && dStr <= toDate;
      if (fromDate) return dStr >= fromDate;
      if (toDate) return dStr <= toDate;
    }
    return true;
  };

  // =========================================================
  // FILTERED DATASETS
  // =========================================================

  // Filtered Wholesalers
  const filteredWholesalers = useMemo(() => {
    return wholesalers.filter((w) => {
      const q = wholesalerSearch.toLowerCase().trim();
      const matchesSearch =
        !q ||
        (w.business_name || "").toLowerCase().includes(q) ||
        (w.store_name || "").toLowerCase().includes(q) ||
        (w.store_code || "").toLowerCase().includes(q) ||
        (w.owner_name || "").toLowerCase().includes(q) ||
        (w.mobile || "").includes(q) ||
        (w.city || "").toLowerCase().includes(q);

      const status = String(w.status || "").toUpperCase();
      let matchesStatus = true;
      if (wholesalerStatusFilter !== "ALL") {
        matchesStatus = status === wholesalerStatusFilter;
      }

      return matchesSearch && matchesStatus;
    });
  }, [wholesalers, wholesalerSearch, wholesalerStatusFilter]);

  // Unique Retail Stores who ordered from this Wholesaler (for filter dropdown)
  const wholesalerBuyerStores = useMemo(() => {
    const map = new Map();
    wholesalerOrders.forEach((o) => {
      const code = o.store_code;
      const name = o.store_name || o.customer_name || code;
      if (code && !map.has(code)) {
        map.set(code, name);
      }
    });
    return Array.from(map.entries()).map(([code, name]) => ({ code, name }));
  }, [wholesalerOrders]);

  // Filtered Wholesaler Orders (Latest to Oldest)
  const filteredWholesalerOrders = useMemo(() => {
    const list = wholesalerOrders.filter((o) => {
      const q = wholesalerOrderSearch.toLowerCase().trim();
      const matchesSearch =
        !q ||
        (o.order_id || "").toLowerCase().includes(q) ||
        (o.store_name || "").toLowerCase().includes(q) ||
        (o.store_code || "").toLowerCase().includes(q) ||
        (o.customer_name || "").toLowerCase().includes(q) ||
        (o.mobile || "").includes(q);

      let matchesBuyer = true;
      if (retailStoreFilter !== "ALL") {
        matchesBuyer = String(o.store_code).toUpperCase() === String(retailStoreFilter).toUpperCase();
      }

      let matchesStatus = true;
      if (wholesalerStatusOrderFilter !== "ALL") {
        matchesStatus = String(o.status || "").toUpperCase() === wholesalerStatusOrderFilter;
      }

      const matchesDate = checkDateInRange(
        o.created_at,
        wholesalerDateFilter,
        wholesalerFromDate,
        wholesalerToDate
      );

      return matchesSearch && matchesBuyer && matchesStatus && matchesDate;
    });

    // STRICT SORTING: Latest (newest) to oldest
    list.sort((a, b) => {
      const timeA = new Date(a.created_at || 0).getTime();
      const timeB = new Date(b.created_at || 0).getTime();
      return timeB - timeA;
    });

    return list;
  }, [
    wholesalerOrders,
    wholesalerOrderSearch,
    retailStoreFilter,
    wholesalerStatusOrderFilter,
    wholesalerDateFilter,
    wholesalerFromDate,
    wholesalerToDate
  ]);

  // Unique Cities of Retail Stores
  const retailCities = useMemo(() => {
    const set = new Set();
    retailStores.forEach((s) => {
      if (s.city) set.add(s.city.trim());
    });
    return Array.from(set).sort();
  }, [retailStores]);

  // Filtered Retail Stores
  const filteredRetailStores = useMemo(() => {
    return retailStores.filter((s) => {
      const q = retailSearch.toLowerCase().trim();
      const matchesSearch =
        !q ||
        (s.store_name || "").toLowerCase().includes(q) ||
        (s.store_code || "").toLowerCase().includes(q) ||
        (s.owner_name || "").toLowerCase().includes(q) ||
        (s.mobile || "").includes(q) ||
        (s.email || "").toLowerCase().includes(q) ||
        (s.city || "").toLowerCase().includes(q);

      let matchesCity = true;
      if (retailCityFilter !== "ALL") {
        matchesCity = (s.city || "").toLowerCase() === retailCityFilter.toLowerCase();
      }

      let matchesStatus = true;
      if (retailStatusFilter !== "ALL") {
        const sub = String(s.subscription_status || s.status || "").toUpperCase();
        matchesStatus = sub === retailStatusFilter;
      }

      return matchesSearch && matchesCity && matchesStatus;
    });
  }, [retailStores, retailSearch, retailCityFilter, retailStatusFilter]);

  // Filtered Retail Eye Tests
  const filteredEyeTests = useMemo(() => {
    const list = eyeTests.filter((e) => {
      const q = eyeTestSearch.toLowerCase().trim();
      const matchesSearch =
        !q ||
        (e.patient_name || "").toLowerCase().includes(q) ||
        (e.patient_id || "").toLowerCase().includes(q) ||
        (e.mobile_number || e.mobile || "").includes(q);

      const matchesDate = checkDateInRange(
        e.exam_date || e.created_at,
        eyeTestDateFilter,
        eyeTestFromDate,
        eyeTestToDate
      );

      return matchesSearch && matchesDate;
    });

    list.sort((a, b) => {
      const timeA = new Date(a.exam_date || a.created_at || 0).getTime();
      const timeB = new Date(b.exam_date || b.created_at || 0).getTime();
      return timeB - timeA;
    });

    return list;
  }, [eyeTests, eyeTestSearch, eyeTestDateFilter, eyeTestFromDate, eyeTestToDate]);

  // Filtered Retail Optical Sales
  const filteredOpticalSales = useMemo(() => {
    const list = opticalSales.filter((o) => {
      const q = opticalSalesSearch.toLowerCase().trim();
      const matchesSearch =
        !q ||
        (o.patient_name || o.name || "").toLowerCase().includes(q) ||
        (o.patient_id || "").toLowerCase().includes(q) ||
        (o.mobile || "").includes(q) ||
        (o.order_no || "").toLowerCase().includes(q) ||
        (o.bill_number || "").toLowerCase().includes(q);

      let matchesStatus = true;
      if (opticalSalesStatusFilter !== "ALL") {
        const norm = getNormalizedOpticalStatus(o);
        if (opticalSalesStatusFilter === "CLOSE" || opticalSalesStatusFilter === "CLOSED") {
          matchesStatus = norm === "CLOSED";
        } else {
          matchesStatus = norm === opticalSalesStatusFilter;
        }
      }

      let matchesPayment = true;
      if (opticalSalesPaymentFilter !== "ALL") {
        const payStatus = String(o.payment_status || (Number(o.balance_amount || 0) <= 0 ? "PAID" : "DUE")).toUpperCase();
        matchesPayment = payStatus === opticalSalesPaymentFilter;
      }

      const matchesDate = checkDateInRange(
        o.order_date || o.created_at,
        opticalSalesDateFilter,
        opticalSalesFromDate,
        opticalSalesToDate
      );

      return matchesSearch && matchesStatus && matchesPayment && matchesDate;
    });

    list.sort((a, b) => {
      const timeA = new Date(a.order_date || a.created_at || 0).getTime();
      const timeB = new Date(b.order_date || b.created_at || 0).getTime();
      return timeB - timeA;
    });

    return list;
  }, [
    opticalSales,
    opticalSalesSearch,
    opticalSalesStatusFilter,
    opticalSalesPaymentFilter,
    opticalSalesDateFilter,
    opticalSalesFromDate,
    opticalSalesToDate
  ]);

  // Filtered Retail Placed E-Commerce Orders
  const filteredRetailEcommOrders = useMemo(() => {
    const list = retailEcommOrders.filter((o) => {
      const q = retailEcommSearch.toLowerCase().trim();
      const matchesSearch =
        !q ||
        (o.order_id || "").toLowerCase().includes(q) ||
        (o.seller_store_code || "").toLowerCase().includes(q) ||
        (o.customer_name || "").toLowerCase().includes(q);

      let matchesStatus = true;
      if (retailEcommStatusFilter !== "ALL") {
        matchesStatus = String(o.status || "").toUpperCase() === retailEcommStatusFilter;
      }

      const matchesDate = checkDateInRange(
        o.created_at,
        retailEcommDateFilter,
        retailEcommFromDate,
        retailEcommToDate
      );

      return matchesSearch && matchesStatus && matchesDate;
    });

    list.sort((a, b) => {
      const timeA = new Date(a.created_at || 0).getTime();
      const timeB = new Date(b.created_at || 0).getTime();
      return timeB - timeA;
    });

    return list;
  }, [
    retailEcommOrders,
    retailEcommSearch,
    retailEcommStatusFilter,
    retailEcommDateFilter,
    retailEcommFromDate,
    retailEcommToDate
  ]);

  // =========================================================
  // EXPORT HANDLERS
  // =========================================================

  const exportWholesalerOrdersCSV = () => {
    const headers = [
      { label: "Order ID", accessor: (r) => r.order_id },
      { label: "Date", accessor: (r) => formatDateToDDMMYYYY(r.created_at) },
      { label: "Buyer Retail Store Name", accessor: (r) => r.store_name || r.customer_name },
      { label: "Buyer Store Code", accessor: (r) => r.store_code },
      { label: "Buyer Contact", accessor: (r) => r.mobile },
      { label: "Delivery Address", accessor: (r) => r.address },
      { label: "Total Amount (₹)", accessor: (r) => r.total_amount },
      { label: "Payment Method", accessor: (r) => r.payment_method },
      { label: "Status", accessor: (r) => r.status },
      { label: "Waybill / Tracking", accessor: (r) => r.waybill_number || "-" },
      { label: "Courier Partner", accessor: (r) => r.delivery_partner || "-" },
      { label: "Items Count", accessor: (r) => Array.isArray(r.items) ? r.items.length : 1 }
    ];
    downloadCSV(
      `wholesaler_orders_${selectedWholesaler?.store_code || "all"}_${getDateOnlyStr(new Date())}.csv`,
      filteredWholesalerOrders,
      headers
    );
  };

  const exportEyeTestsCSV = () => {
    const headers = [
      { label: "Test ID", accessor: (r) => r.id },
      { label: "Date", accessor: (r) => formatDateToDDMMYYYY(r.exam_date || r.created_at) },
      { label: "Patient Name", accessor: (r) => r.patient_name },
      { label: "Patient ID", accessor: (r) => r.patient_id || "-" },
      { label: "Mobile", accessor: (r) => r.mobile_number || r.mobile || "-" },
      { label: "Age", accessor: (r) => r.age || "-" },
      { label: "Gender", accessor: (r) => r.gender || "-" },
      { label: "Right Eye SPH", accessor: (r) => r.right_sph || "-" },
      { label: "Right Eye CYL", accessor: (r) => r.right_cyl || "-" },
      { label: "Right Eye AXIS", accessor: (r) => r.right_axis || "-" },
      { label: "Right Eye ADD", accessor: (r) => r.right_add || "-" },
      { label: "Right Eye V/A", accessor: (r) => r.right_va || "-" },
      { label: "Left Eye SPH", accessor: (r) => r.left_sph || "-" },
      { label: "Left Eye CYL", accessor: (r) => r.left_cyl || "-" },
      { label: "Left Eye AXIS", accessor: (r) => r.left_axis || "-" },
      { label: "Left Eye ADD", accessor: (r) => r.left_add || "-" },
      { label: "Left Eye V/A", accessor: (r) => r.left_va || "-" },
      { label: "IPD / PD", accessor: (r) => r.pd || "-" },
      { label: "Lens Recommendation", accessor: (r) => r.lens_recommendation || r.lens || "-" },
      { label: "Clinical Notes", accessor: (r) => r.notes || r.instructions || "-" }
    ];
    downloadCSV(
      `eyetests_${selectedRetailStore?.store_code || "store"}_${getDateOnlyStr(new Date())}.csv`,
      filteredEyeTests,
      headers
    );
  };

  const exportOpticalSalesCSV = () => {
    const headers = [
      { label: "Order / Bill No", accessor: (r) => r.bill_number || r.order_no || `#ORD-${r.id}` },
      { label: "Date", accessor: (r) => formatDateToDDMMYYYY(r.order_date || r.created_at) },
      { label: "Patient Name", accessor: (r) => r.patient_name || r.name || "Customer" },
      { label: "Patient ID", accessor: (r) => r.patient_id || "-" },
      { label: "Mobile", accessor: (r) => r.mobile || "-" },
      { label: "Frame Model", accessor: (r) => r.frame_model || "-" },
      { label: "Lens Type", accessor: (r) => r.lens_type || "-" },
      { label: "Total Amount (₹)", accessor: (r) => r.total_amount },
      { label: "Advance Paid (₹)", accessor: (r) => r.advance_paid || 0 },
      { label: "Balance Due (₹)", accessor: (r) => r.balance_amount !== undefined ? r.balance_amount : (Number(r.total_amount || 0) - Number(r.advance_paid || 0)) },
      { label: "Payment Mode", accessor: (r) => r.payment_mode || "Cash" },
      { label: "Payment Status", accessor: (r) => r.payment_status || (Number(r.balance_amount || 0) <= 0 ? "Paid" : "Due") },
      { label: "Order Status", accessor: (r) => getNormalizedOpticalStatus(r) }
    ];
    downloadCSV(
      `optical_sales_${selectedRetailStore?.store_code || "store"}_${getDateOnlyStr(new Date())}.csv`,
      filteredOpticalSales,
      headers
    );
  };

  const exportRetailEcommOrdersCSV = () => {
    const headers = [
      { label: "Order ID", accessor: (r) => r.order_id },
      { label: "Date", accessor: (r) => formatDateToDDMMYYYY(r.created_at) },
      { label: "Supplier Store Code", accessor: (r) => r.seller_store_code },
      { label: "Total Amount (₹)", accessor: (r) => r.total_amount },
      { label: "Payment Method", accessor: (r) => r.payment_method },
      { label: "Status", accessor: (r) => r.status },
      { label: "Waybill / Tracking", accessor: (r) => r.waybill_number || "-" },
      { label: "Courier Partner", accessor: (r) => r.delivery_partner || "-" },
      { label: "Items Count", accessor: (r) => Array.isArray(r.items) ? r.items.length : 1 }
    ];
    downloadCSV(
      `placed_b2b_orders_${selectedRetailStore?.store_code || "store"}_${getDateOnlyStr(new Date())}.csv`,
      filteredRetailEcommOrders,
      headers
    );
  };

  // Print Bill Helper
  const handlePrintDocument = (printableElementId) => {
    const printContent = document.getElementById(printableElementId);
    if (!printContent) return;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Document Print</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; margin: 20px; color: #0f172a; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #cbd5e1; padding: 6px 10px; text-align: left; font-size: 11px; }
            th { background: #f1f5f9; font-weight: 700; color: #1e293b; }
            .header-title { font-size: 18px; font-weight: 800; color: #0067b1; text-transform: uppercase; margin-bottom: 2px; }
            .header-sub { font-size: 12px; color: #64748b; margin-bottom: 12px; }
            .meta-box { border: 1px solid #cbd5e1; padding: 10px; border-radius: 6px; margin-bottom: 12px; font-size: 12px; }
            .summary-box { background: #f8fafc; border: 1.5px solid #0067b1; padding: 10px; border-radius: 6px; margin-top: 14px; }
            @media print { button { display: none; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 400);
  };

  // Helper status badge styles
  const getStatusBadge = (statusStr) => {
    const s = String(statusStr || "PENDING").toUpperCase();
    if (s === "APPROVED" || s === "ACTIVE" || s === "DELIVERED" || s === "COMPLETED" || s === "PAID") {
      return { bg: "#DCFCE7", text: "#166534", border: "#86EFAC" };
    }
    if (s === "SHIPPED" || s === "CONFIRMED" || s === "CLOSED") {
      return { bg: "#E0F2FE", text: "#0369A1", border: "#7DD3FC" };
    }
    if (s === "PENDING" || s === "ORDERED" || s === "OPEN" || s === "DUE") {
      return { bg: "#FEF3C7", text: "#92400E", border: "#FCD34D" };
    }
    if (s === "REJECTED" || s === "CANCELLED" || s === "EXPIRED" || s === "SUSPENDED") {
      return { bg: "#FEE2E2", text: "#991B1B", border: "#FCA5A5" };
    }
    return { bg: "#F1F5F9", text: "#475569", border: "#CBD5E1" };
  };

  return (
    <div className="store-directory-container">
      {/* ========================================================= */}
      {/* TOP HEADER & BREADCRUMBS */}
      {/* ========================================================= */}
      <div className="directory-header-card">
        <div className="directory-header-row">
          <div>
            <div className="breadcrumb-nav">
              <span
                className="breadcrumb-link"
                onClick={() => {
                  if (selectedWholesaler) setSelectedWholesaler(null);
                  if (selectedRetailStore) setSelectedRetailStore(null);
                }}
              >
                Store Directory
              </span>
              {selectedWholesaler && (
                <>
                  <span className="breadcrumb-sep">/</span>
                  <span className="breadcrumb-link" onClick={() => setSelectedWholesaler(null)}>
                    Wholesalers
                  </span>
                  <span className="breadcrumb-sep">/</span>
                  <span className="breadcrumb-current">
                    {selectedWholesaler.business_name || selectedWholesaler.store_name} ({selectedWholesaler.store_code})
                  </span>
                </>
              )}
              {selectedRetailStore && (
                <>
                  <span className="breadcrumb-sep">/</span>
                  <span className="breadcrumb-link" onClick={() => setSelectedRetailStore(null)}>
                    Retail Stores
                  </span>
                  <span className="breadcrumb-sep">/</span>
                  <span className="breadcrumb-current">
                    {selectedRetailStore.store_name} ({selectedRetailStore.store_code})
                  </span>
                </>
              )}
            </div>

            <h1 className="directory-page-title">
              {selectedWholesaler
                ? `${selectedWholesaler.business_name || selectedWholesaler.store_name} — Wholesale Orders Hub`
                : selectedRetailStore
                ? `${selectedRetailStore.store_name} — Store Intelligence Hub`
                : "Optical Store & Wholesaler Directory"}
            </h1>
            <p className="directory-page-subtitle">
              {selectedWholesaler
                ? `Inspect and filter all B2B orders received from retail optical stores across the network.`
                : selectedRetailStore
                ? `Inspect clinical eye examinations, individual optical sale receipts, and placed B2B orders for this retail store.`
                : "Super Admin Centralized Hub: View any store's e-commerce orders, clinical eye tests, and optical sales."}
            </p>
          </div>

          {/* Back button when inside a store detail */}
          {(selectedWholesaler || selectedRetailStore) && (
            <button
              className="back-btn-pill"
              onClick={() => {
                if (selectedWholesaler) setSelectedWholesaler(null);
                if (selectedRetailStore) setSelectedRetailStore(null);
              }}
            >
              <ArrowLeft size={16} />
              <span>Back to Directory</span>
            </button>
          )}
        </div>

        {/* MAIN ROOT TABS (Wholesalers vs Retail Stores) */}
        {!selectedWholesaler && !selectedRetailStore && (
          <div className="main-tabs-wrapper">
            <button
              className={`main-tab-btn ${mainTab === "wholesalers" ? "active" : ""}`}
              onClick={() => setMainTab("wholesalers")}
            >
              <Building2 size={18} />
              <span>Wholesalers Directory</span>
              <span className="tab-count-pill">{wholesalers.length}</span>
            </button>

            <button
              className={`main-tab-btn ${mainTab === "retail_stores" ? "active" : ""}`}
              onClick={() => setMainTab("retail_stores")}
            >
              <Store size={18} />
              <span>Retail Stores Directory</span>
              <span className="tab-count-pill">{retailStores.length}</span>
            </button>
          </div>
        )}
      </div>

      {/* ========================================================= */}
      {/* SECTION 1: WHOLESALERS DIRECTORY (ROOT VIEW) */}
      {/* ========================================================= */}
      {!selectedWholesaler && !selectedRetailStore && mainTab === "wholesalers" && (
        <div className="directory-content-section">
          {/* Quick KPI Strip */}
          <div className="kpi-strip-grid">
            <div className="kpi-chip">
              <span className="kpi-chip-label">Total Wholesalers</span>
              <span className="kpi-chip-val">{wholesalers.length}</span>
            </div>
            <div className="kpi-chip">
              <span className="kpi-chip-label">Approved Suppliers</span>
              <span className="kpi-chip-val text-success">
                {wholesalers.filter((w) => String(w.status).toUpperCase() === "APPROVED").length}
              </span>
            </div>
            <div className="kpi-chip">
              <span className="kpi-chip-label">Pending Approval</span>
              <span className="kpi-chip-val text-warning">
                {wholesalers.filter((w) => String(w.status).toUpperCase() === "PENDING").length}
              </span>
            </div>
          </div>

          {/* Search & Status Filters */}
          <div className="filter-card-bar">
            <div className="search-input-box">
              <Search size={18} color="#64748B" />
              <input
                type="text"
                placeholder="Search wholesalers by business name, code, owner, mobile, city..."
                value={wholesalerSearch}
                onChange={(e) => setWholesalerSearch(e.target.value)}
              />
              {wholesalerSearch && (
                <button className="clear-icon-btn" onClick={() => setWholesalerSearch("")}>
                  <X size={15} />
                </button>
              )}
            </div>

            <div className="status-pills-row">
              {["ALL", "APPROVED", "PENDING", "REJECTED"].map((st) => (
                <button
                  key={st}
                  className={`status-filter-pill ${wholesalerStatusFilter === st ? "active" : ""}`}
                  onClick={() => setWholesalerStatusFilter(st)}
                >
                  {st === "ALL" ? "All Status" : st}
                </button>
              ))}
            </div>
          </div>

          {/* Wholesalers Grid */}
          {wholesalersLoading ? (
            <div className="loading-box">
              <RefreshCw className="spin-icon" size={32} color="#0067b1" />
              <p>Loading registered wholesalers...</p>
            </div>
          ) : filteredWholesalers.length === 0 ? (
            <div className="empty-box">
              <Building2 size={42} color="#94A3B8" />
              <h3>No Wholesalers Found</h3>
              <p>Try adjusting your search query or status filter.</p>
            </div>
          ) : (
            <div className="stores-cards-grid">
              {filteredWholesalers.map((w) => {
                const badge = getStatusBadge(w.status);
                return (
                  <div
                    key={w.id || w.store_code}
                    className="store-overview-card"
                    onClick={() => handleSelectWholesaler(w)}
                  >
                    <div className="card-top-header">
                      <div className="store-avatar-box wholesaler-avatar">
                        <Building2 size={22} color="#0F766E" />
                      </div>
                      <div className="store-info-col">
                        <div className="store-title-badge-row">
                          <h3 className="store-card-name" title={w.business_name || w.store_name}>
                            {w.business_name || w.store_name || "Wholesale Partner"}
                          </h3>
                        </div>
                        <span className="store-code-tag">{w.store_code}</span>
                      </div>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: badge.bg, color: badge.text, borderColor: badge.border }}
                      >
                        {w.status || "APPROVED"}
                      </span>
                    </div>

                    <div className="card-divider" />

                    <div className="store-meta-details">
                      <div className="meta-line">
                        <User size={14} color="#64748B" />
                        <span>Owner: <strong>{w.owner_name || "-"}</strong></span>
                      </div>
                      <div className="meta-line">
                        <Phone size={14} color="#64748B" />
                        <span>Contact: <strong>{w.mobile || "-"}</strong></span>
                      </div>
                      <div className="meta-line">
                        <MapPin size={14} color="#64748B" />
                        <span>City: <strong>{w.city || w.address || "-"}</strong></span>
                      </div>
                      {w.gst_number && (
                        <div className="meta-line">
                          <Hash size={14} color="#64748B" />
                          <span>GST: <strong>{w.gst_number}</strong></span>
                        </div>
                      )}
                    </div>

                    <div className="card-footer-action">
                      <span className="click-action-text">View Wholesale Orders</span>
                      <ChevronRight size={16} color="#0067b1" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ========================================================= */}
      {/* SECTION 2: SELECTED WHOLESALER ORDERS HUB */}
      {/* ========================================================= */}
      {selectedWholesaler && (
        <div className="directory-content-section">
          {/* Wholesaler Profile Card */}
          <div className="wholesaler-hero-card">
            <div className="hero-profile-row">
              <div className="wholesaler-large-avatar">
                <Building2 size={32} color="#FFFFFF" />
              </div>
              <div className="hero-profile-info">
                <div className="hero-title-row">
                  <h2>{selectedWholesaler.business_name || selectedWholesaler.store_name}</h2>
                  <span className="store-code-pill-hero">{selectedWholesaler.store_code}</span>
                  <span
                    className="status-badge"
                    style={{
                      ...getStatusBadge(selectedWholesaler.status),
                      padding: "4px 10px",
                      fontSize: "12px"
                    }}
                  >
                    {selectedWholesaler.status || "APPROVED"}
                  </span>
                </div>
                <div className="hero-meta-grid">
                  <span><strong>Owner:</strong> {selectedWholesaler.owner_name || "-"}</span>
                  <span><strong>Phone:</strong> {selectedWholesaler.mobile || "-"}</span>
                  <span><strong>Email:</strong> {selectedWholesaler.email || "-"}</span>
                  <span><strong>Location:</strong> {selectedWholesaler.city || selectedWholesaler.address || "-"}</span>
                  {selectedWholesaler.gst_number && (
                    <span><strong>GST:</strong> {selectedWholesaler.gst_number}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Wholesaler Orders Filter Strip */}
          <div className="filters-strip-container">
            <div className="filters-top-row">
              <div className="search-box-flex">
                <Search size={16} color="#64748B" />
                <input
                  type="text"
                  placeholder="Search by order ID, buyer store name, contact..."
                  value={wholesalerOrderSearch}
                  onChange={(e) => setWholesalerOrderSearch(e.target.value)}
                />
                {wholesalerOrderSearch && (
                  <button className="clear-icon-btn" onClick={() => setWholesalerOrderSearch("")}>
                    <X size={14} />
                  </button>
                )}
              </div>

              {/* Filter by Retail Store Dropdown */}
              <div className="select-dropdown-wrap">
                <span className="dropdown-label">Buyer Store:</span>
                <select
                  value={retailStoreFilter}
                  onChange={(e) => setRetailStoreFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="ALL">All Retail Stores ({wholesalerBuyerStores.length})</option>
                  {wholesalerBuyerStores.map((b) => (
                    <option key={b.code} value={b.code}>
                      {b.name} ({b.code})
                    </option>
                  ))}
                </select>
              </div>

              {/* Filter by Order Status */}
              <div className="select-dropdown-wrap">
                <span className="dropdown-label">Status:</span>
                <select
                  value={wholesalerStatusOrderFilter}
                  onChange={(e) => setWholesalerStatusOrderFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="ORDERED">Ordered</option>
                  <option value="CONFIRMED">Confirmed</option>
                  <option value="SHIPPED">Shipped</option>
                  <option value="DELIVERED">Delivered</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>

              {/* Export CSV Button */}
              <button className="export-csv-btn" onClick={exportWholesalerOrdersCSV}>
                <Download size={15} />
                <span>Export CSV</span>
              </button>
            </div>

            {/* Date Range Bar */}
            <div className="filters-bottom-row">
              <div className="quick-dates-group">
                <span className="dates-label">Date Range:</span>
                {[
                  { id: "ALL", label: "All Time" },
                  { id: "TODAY", label: "Today" },
                  { id: "WEEK", label: "This Week" },
                  { id: "MONTH", label: "This Month" },
                  { id: "RANGE", label: "Custom Range" }
                ].map((d) => (
                  <button
                    key={d.id}
                    className={`date-pill-btn ${wholesalerDateFilter === d.id ? "active" : ""}`}
                    onClick={() => {
                      setWholesalerDateFilter(d.id);
                      if (d.id !== "RANGE") {
                        setWholesalerFromDate("");
                        setWholesalerToDate("");
                      }
                    }}
                  >
                    {d.label}
                  </button>
                ))}
              </div>

              {wholesalerDateFilter === "RANGE" && (
                <div className="custom-date-inputs">
                  <div className="date-input-field">
                    <span>From:</span>
                    <input
                      type="date"
                      value={wholesalerFromDate}
                      onChange={(e) => setWholesalerFromDate(e.target.value)}
                    />
                  </div>
                  <div className="date-input-field">
                    <span>To:</span>
                    <input
                      type="date"
                      value={wholesalerToDate}
                      onChange={(e) => setWholesalerToDate(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <span className="records-count-indicator">
                Showing <strong>{filteredWholesalerOrders.length}</strong> wholesale orders (Latest to Oldest)
              </span>
            </div>
          </div>

          {/* Orders Table & Cards */}
          {wholesalerOrdersLoading ? (
            <div className="loading-box">
              <RefreshCw className="spin-icon" size={30} color="#0067b1" />
              <p>Fetching orders for {selectedWholesaler.business_name}...</p>
            </div>
          ) : filteredWholesalerOrders.length === 0 ? (
            <div className="empty-box">
              <ShoppingBag size={40} color="#94A3B8" />
              <h3>No Wholesale Orders Found</h3>
              <p>No orders matched the selected filter criteria or date range.</p>
            </div>
          ) : (
            <div className="orders-data-card">
              <div className="table-responsive-container">
                <table className="custom-data-table">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Date</th>
                      <th>Buyer Retail Store</th>
                      <th>Buyer Code</th>
                      <th>Items Summary</th>
                      <th>Total Value</th>
                      <th>Payment</th>
                      <th>Status</th>
                      <th>Waybill / Courier</th>
                      <th style={{ textAlign: "center" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredWholesalerOrders.map((ord) => {
                      const badge = getStatusBadge(ord.status);
                      const itemsArr = Array.isArray(ord.items) ? ord.items : [];
                      return (
                        <tr key={ord.order_id || ord.id}>
                          <td>
                            <strong className="order-id-highlight">{ord.order_id}</strong>
                          </td>
                          <td>{formatDateToDDMMYYYY(ord.created_at)}</td>
                          <td>
                            <strong style={{ color: "#0F172A" }}>
                              {ord.store_name || ord.customer_name || "Retail Store"}
                            </strong>
                            <div className="sub-table-text">{ord.mobile || "-"}</div>
                          </td>
                          <td>
                            <span className="buyer-code-badge">{ord.store_code}</span>
                          </td>
                          <td>
                            <div className="items-summary-preview">
                              <span>{itemsArr.length} items</span>
                              {itemsArr[0] && (
                                <span className="item-name-preview" title={itemsArr[0].product_name}>
                                  • {itemsArr[0].product_name}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <strong style={{ color: "#0067B1" }}>
                              ₹{Number(ord.total_amount || 0).toLocaleString()}
                            </strong>
                          </td>
                          <td>
                            <span className="payment-pill">{ord.payment_method || "Cash"}</span>
                          </td>
                          <td>
                            <span
                              className="status-badge"
                              style={{ backgroundColor: badge.bg, color: badge.text, borderColor: badge.border }}
                            >
                              {ord.status || "ORDERED"}
                            </span>
                          </td>
                          <td>
                            {ord.waybill_number ? (
                              <div className="waybill-tag">
                                <Truck size={12} />
                                <span>{ord.waybill_number}</span>
                              </div>
                            ) : (
                              <span style={{ color: "#94A3B8", fontSize: "11px" }}>Not Dispatched</span>
                            )}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <button
                              className="view-details-action-btn"
                              onClick={() => setSelectedOrderDetails(ord)}
                            >
                              <Eye size={13} />
                              <span>View Order</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========================================================= */}
      {/* SECTION 3: RETAIL STORES DIRECTORY (ROOT VIEW) */}
      {/* ========================================================= */}
      {!selectedWholesaler && !selectedRetailStore && mainTab === "retail_stores" && (
        <div className="directory-content-section">
          {/* Quick KPI Strip */}
          <div className="kpi-strip-grid">
            <div className="kpi-chip">
              <span className="kpi-chip-label">Total Retail Stores</span>
              <span className="kpi-chip-val">{retailStores.length}</span>
            </div>
            <div className="kpi-chip">
              <span className="kpi-chip-label">Active Subscriptions</span>
              <span className="kpi-chip-val text-success">
                {retailStores.filter((s) => String(s.subscription_status || s.status).toUpperCase() === "ACTIVE").length}
              </span>
            </div>
            <div className="kpi-chip">
              <span className="kpi-chip-label">Cities Covered</span>
              <span className="kpi-chip-val text-primary">{retailCities.length}</span>
            </div>
          </div>

          {/* Search & Filters Bar */}
          <div className="filter-card-bar">
            <div className="search-input-box">
              <Search size={18} color="#64748B" />
              <input
                type="text"
                placeholder="Search retail stores by store name, code, owner, mobile, email, city..."
                value={retailSearch}
                onChange={(e) => setRetailSearch(e.target.value)}
              />
              {retailSearch && (
                <button className="clear-icon-btn" onClick={() => setRetailSearch("")}>
                  <X size={15} />
                </button>
              )}
            </div>

            {/* City Filter */}
            <div className="select-dropdown-wrap">
              <span className="dropdown-label">City:</span>
              <select
                value={retailCityFilter}
                onChange={(e) => setRetailCityFilter(e.target.value)}
                className="filter-select"
              >
                <option value="ALL">All Cities ({retailCities.length})</option>
                {retailCities.map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>

            {/* Subscription Status Filter */}
            <div className="select-dropdown-wrap">
              <span className="dropdown-label">Plan Status:</span>
              <select
                value={retailStatusFilter}
                onChange={(e) => setRetailStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="ALL">All Subscription Status</option>
                <option value="ACTIVE">Active</option>
                <option value="PENDING">Pending</option>
                <option value="EXPIRED">Expired</option>
                <option value="SUSPENDED">Suspended</option>
              </select>
            </div>
          </div>

          {/* Retail Stores Grid */}
          {retailStoresLoading ? (
            <div className="loading-box">
              <RefreshCw className="spin-icon" size={32} color="#0067b1" />
              <p>Loading retail stores network...</p>
            </div>
          ) : filteredRetailStores.length === 0 ? (
            <div className="empty-box">
              <Store size={42} color="#94A3B8" />
              <h3>No Retail Stores Found</h3>
              <p>Try adjusting your search query or city/status filters.</p>
            </div>
          ) : (
            <div className="stores-cards-grid">
              {filteredRetailStores.map((s) => {
                const badge = getStatusBadge(s.subscription_status || s.status);
                return (
                  <div
                    key={s.id || s.store_code}
                    className="store-overview-card retail-card"
                    onClick={() => handleSelectRetailStore(s)}
                  >
                    <div className="card-top-header">
                      <div className="store-avatar-box retail-avatar">
                        <Store size={22} color="#2563EB" />
                      </div>
                      <div className="store-info-col">
                        <h3 className="store-card-name" title={s.store_name}>
                          {s.store_name}
                        </h3>
                        <span className="store-code-tag">{s.store_code}</span>
                      </div>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: badge.bg, color: badge.text, borderColor: badge.border }}
                      >
                        {s.subscription_status || s.status || "ACTIVE"}
                      </span>
                    </div>

                    <div className="card-divider" />

                    <div className="store-meta-details">
                      <div className="meta-line">
                        <User size={14} color="#64748B" />
                        <span>Owner: <strong>{s.owner_name || "-"}</strong></span>
                      </div>
                      <div className="meta-line">
                        <Phone size={14} color="#64748B" />
                        <span>Phone: <strong>{s.mobile || "-"}</strong></span>
                      </div>
                      <div className="meta-line">
                        <MapPin size={14} color="#64748B" />
                        <span>City: <strong>{s.city || s.address || "-"}</strong></span>
                      </div>
                      {s.plan_name && (
                        <div className="meta-line">
                          <CreditCard size={14} color="#64748B" />
                          <span>Plan: <strong style={{ color: "#0067B1" }}>{s.plan_name}</strong></span>
                        </div>
                      )}
                    </div>

                    <div className="card-footer-action">
                      <span className="click-action-text">Inspect Store Records</span>
                      <ChevronRight size={16} color="#0067b1" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ========================================================= */}
      {/* SECTION 4: SELECTED RETAIL STORE HUB (3 TABS) */}
      {/* ========================================================= */}
      {selectedRetailStore && (
        <div className="directory-content-section">
          {/* Retail Store Profile Hero Card */}
          <div className="retail-hero-card">
            <div className="hero-profile-row">
              <div className="retail-large-avatar">
                <Store size={32} color="#FFFFFF" />
              </div>
              <div className="hero-profile-info">
                <div className="hero-title-row">
                  <h2>{selectedRetailStore.store_name}</h2>
                  <span className="store-code-pill-hero">{selectedRetailStore.store_code}</span>
                  <span
                    className="status-badge"
                    style={{
                      ...getStatusBadge(selectedRetailStore.subscription_status || selectedRetailStore.status),
                      padding: "4px 10px",
                      fontSize: "12px"
                    }}
                  >
                    {selectedRetailStore.subscription_status || selectedRetailStore.status || "ACTIVE"}
                  </span>
                </div>
                <div className="hero-meta-grid">
                  <span><strong>Owner:</strong> {selectedRetailStore.owner_name || "-"}</span>
                  <span><strong>Mobile:</strong> {selectedRetailStore.mobile || "-"}</span>
                  <span><strong>Email:</strong> {selectedRetailStore.email || "-"}</span>
                  <span><strong>City:</strong> {selectedRetailStore.city || "-"}</span>
                  <span><strong>Address:</strong> {selectedRetailStore.address || "-"}</span>
                  {selectedRetailStore.plan_name && (
                    <span><strong>Subscription:</strong> {selectedRetailStore.plan_name}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 3 Sub-Tabs Navigation for this Retail Store */}
          <div className="retail-subtabs-bar">
            <button
              className={`subtab-btn ${retailSubTab === "eyetests" ? "active" : ""}`}
              onClick={() => setRetailSubTab("eyetests")}
            >
              <Eye size={17} />
              <span>1. Eye Examinations</span>
              <span className="subtab-count-badge">{eyeTests.length}</span>
            </button>

            <button
              className={`subtab-btn ${retailSubTab === "optical_sales" ? "active" : ""}`}
              onClick={() => setRetailSubTab("optical_sales")}
            >
              <FileText size={17} />
              <span>2. Optical Sales & Orders</span>
              <span className="subtab-count-badge">{opticalSales.length}</span>
            </button>

            <button
              className={`subtab-btn ${retailSubTab === "ecommerce_orders" ? "active" : ""}`}
              onClick={() => setRetailSubTab("ecommerce_orders")}
            >
              <ShoppingBag size={17} />
              <span>3. Placed E-Commerce Orders</span>
              <span className="subtab-count-badge">{retailEcommOrders.length}</span>
            </button>
          </div>

          {/* --------------------------------------------------------- */}
          {/* SUB-TAB 1: EYE EXAMINATIONS */}
          {/* --------------------------------------------------------- */}
          {retailSubTab === "eyetests" && (
            <div className="subtab-content-container">
              {/* Filter bar for Eye Tests */}
              <div className="filters-strip-container">
                <div className="filters-top-row">
                  <div className="search-box-flex">
                    <Search size={16} color="#64748B" />
                    <input
                      type="text"
                      placeholder="Search patient name, mobile number, patient ID..."
                      value={eyeTestSearch}
                      onChange={(e) => setEyeTestSearch(e.target.value)}
                    />
                    {eyeTestSearch && (
                      <button className="clear-icon-btn" onClick={() => setEyeTestSearch("")}>
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  <button className="export-csv-btn" onClick={exportEyeTestsCSV}>
                    <Download size={15} />
                    <span>Download All Eye Tests (CSV)</span>
                  </button>
                </div>

                <div className="filters-bottom-row">
                  <div className="quick-dates-group">
                    <span className="dates-label">Date Filter:</span>
                    {[
                      { id: "ALL", label: "All Time" },
                      { id: "TODAY", label: "Today" },
                      { id: "WEEK", label: "This Week" },
                      { id: "MONTH", label: "This Month" },
                      { id: "RANGE", label: "Custom Range" }
                    ].map((d) => (
                      <button
                        key={d.id}
                        className={`date-pill-btn ${eyeTestDateFilter === d.id ? "active" : ""}`}
                        onClick={() => {
                          setEyeTestDateFilter(d.id);
                          if (d.id !== "RANGE") {
                            setEyeTestFromDate("");
                            setEyeTestToDate("");
                          }
                        }}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>

                  {eyeTestDateFilter === "RANGE" && (
                    <div className="custom-date-inputs">
                      <div className="date-input-field">
                        <span>From:</span>
                        <input
                          type="date"
                          value={eyeTestFromDate}
                          onChange={(e) => setEyeTestFromDate(e.target.value)}
                        />
                      </div>
                      <div className="date-input-field">
                        <span>To:</span>
                        <input
                          type="date"
                          value={eyeTestToDate}
                          onChange={(e) => setEyeTestToDate(e.target.value)}
                        />
                      </div>
                    </div>
                  )}

                  <span className="records-count-indicator">
                    Showing <strong>{filteredEyeTests.length}</strong> eye test records
                  </span>
                </div>
              </div>

              {/* Eye Tests Table */}
              {eyeTestsLoading ? (
                <div className="loading-box">
                  <RefreshCw className="spin-icon" size={28} color="#0067b1" />
                  <p>Loading eye test examinations...</p>
                </div>
              ) : filteredEyeTests.length === 0 ? (
                <div className="empty-box">
                  <Eye size={40} color="#94A3B8" />
                  <h3>No Eye Examinations Found</h3>
                  <p>No eye tests recorded or matched your search and date range.</p>
                </div>
              ) : (
                <div className="orders-data-card">
                  <div className="table-responsive-container">
                    <table className="custom-data-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Patient Name</th>
                          <th>Patient ID</th>
                          <th>Mobile</th>
                          <th>Age / Gender</th>
                          <th>OD (Right Eye)</th>
                          <th>OS (Left Eye)</th>
                          <th>Lens Recommendation</th>
                          <th style={{ textAlign: "center" }}>Prescription</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEyeTests.map((e) => (
                          <tr key={e.id}>
                            <td>
                              <strong>{formatDateToDDMMYYYY(e.exam_date || e.created_at)}</strong>
                            </td>
                            <td>
                              <strong style={{ color: "#0F172A" }}>{e.patient_name}</strong>
                            </td>
                            <td>
                              <span className="patient-id-badge">{e.patient_id || "-"}</span>
                            </td>
                            <td>{e.mobile_number || e.mobile || "-"}</td>
                            <td>
                              {e.age ? `${e.age} Yrs` : "-"} / {e.gender || "-"}
                            </td>
                            <td>
                              <div className="refraction-preview-pill">
                                SPH: {e.right_sph || "0.00"} | CYL: {e.right_cyl || "0.00"} {e.right_axis ? `x${e.right_axis}°` : ""}
                              </div>
                            </td>
                            <td>
                              <div className="refraction-preview-pill">
                                SPH: {e.left_sph || "0.00"} | CYL: {e.left_cyl || "0.00"} {e.left_axis ? `x${e.left_axis}°` : ""}
                              </div>
                            </td>
                            <td>
                              <span className="lens-rec-text" title={e.lens_recommendation || e.lens || "-"}>
                                {e.lens_recommendation || e.lens || "-"}
                              </span>
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <button
                                className="view-details-action-btn"
                                onClick={() => setSelectedEyeTestModal(e)}
                              >
                                <Eye size={13} />
                                <span>View Test</span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* --------------------------------------------------------- */}
          {/* SUB-TAB 2: OPTICAL SALES & ORDERS */}
          {/* --------------------------------------------------------- */}
          {retailSubTab === "optical_sales" && (
            <div className="subtab-content-container">
              {/* Filter bar for Optical Sales */}
              <div className="filters-strip-container">
                <div className="filters-top-row">
                  <div className="search-box-flex">
                    <Search size={16} color="#64748B" />
                    <input
                      type="text"
                      placeholder="Search bill no, order no, customer name, mobile..."
                      value={opticalSalesSearch}
                      onChange={(e) => setOpticalSalesSearch(e.target.value)}
                    />
                    {opticalSalesSearch && (
                      <button className="clear-icon-btn" onClick={() => setOpticalSalesSearch("")}>
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  {/* Order Status Filter */}
                  <div className="select-dropdown-wrap">
                    <span className="dropdown-label">Status:</span>
                    <select
                      value={opticalSalesStatusFilter}
                      onChange={(e) => setOpticalSalesStatusFilter(e.target.value)}
                      className="filter-select"
                    >
                      <option value="ALL">All Status</option>
                      <option value="OPEN">Open</option>
                      <option value="CLOSED">Close</option>
                      <option value="DELIVERED">Delivered</option>
                    </select>
                  </div>

                  {/* Payment Status Filter */}
                  <div className="select-dropdown-wrap">
                    <span className="dropdown-label">Payment:</span>
                    <select
                      value={opticalSalesPaymentFilter}
                      onChange={(e) => setOpticalSalesPaymentFilter(e.target.value)}
                      className="filter-select"
                    >
                      <option value="ALL">All Payments</option>
                      <option value="PAID">Paid</option>
                      <option value="DUE">Due Balance</option>
                    </select>
                  </div>

                  <button className="export-csv-btn" onClick={exportOpticalSalesCSV}>
                    <Download size={15} />
                    <span>Download All Sales (CSV)</span>
                  </button>
                </div>

                <div className="filters-bottom-row">
                  <div className="quick-dates-group">
                    <span className="dates-label">Date Filter:</span>
                    {[
                      { id: "ALL", label: "All Time" },
                      { id: "TODAY", label: "Today" },
                      { id: "WEEK", label: "This Week" },
                      { id: "MONTH", label: "This Month" },
                      { id: "RANGE", label: "Custom Range" }
                    ].map((d) => (
                      <button
                        key={d.id}
                        className={`date-pill-btn ${opticalSalesDateFilter === d.id ? "active" : ""}`}
                        onClick={() => {
                          setOpticalSalesDateFilter(d.id);
                          if (d.id !== "RANGE") {
                            setOpticalSalesFromDate("");
                            setOpticalSalesToDate("");
                          }
                        }}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>

                  {opticalSalesDateFilter === "RANGE" && (
                    <div className="custom-date-inputs">
                      <div className="date-input-field">
                        <span>From:</span>
                        <input
                          type="date"
                          value={opticalSalesFromDate}
                          onChange={(e) => setOpticalSalesFromDate(e.target.value)}
                        />
                      </div>
                      <div className="date-input-field">
                        <span>To:</span>
                        <input
                          type="date"
                          value={opticalSalesToDate}
                          onChange={(e) => setOpticalSalesToDate(e.target.value)}
                        />
                      </div>
                    </div>
                  )}

                  <span className="records-count-indicator">
                    Showing <strong>{filteredOpticalSales.length}</strong> optical sales orders
                  </span>
                </div>
              </div>

              {/* Optical Sales Table */}
              {opticalSalesLoading ? (
                <div className="loading-box">
                  <RefreshCw className="spin-icon" size={28} color="#0067b1" />
                  <p>Loading optical sales orders...</p>
                </div>
              ) : filteredOpticalSales.length === 0 ? (
                <div className="empty-box">
                  <FileText size={40} color="#94A3B8" />
                  <h3>No Optical Sales Found</h3>
                  <p>No optical orders matched your search and filter criteria.</p>
                </div>
              ) : (
                <div className="orders-data-card">
                  <div className="table-responsive-container">
                    <table className="custom-data-table">
                      <thead>
                        <tr>
                          <th>Bill / Order No</th>
                          <th>Date</th>
                          <th>Customer Name</th>
                          <th>Mobile</th>
                          <th>Total Amount</th>
                          <th>Paid</th>
                          <th>Balance Due</th>
                          <th>Payment Mode</th>
                          <th>Status</th>
                          <th style={{ textAlign: "center" }}>Bill View</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOpticalSales.map((o) => {
                          const normStatus = getNormalizedOpticalStatus(o);
                          const badge = getStatusBadge(normStatus);
                          const total = Number(o.total_amount || 0);
                          const paid = Number(o.advance_paid || 0);
                          const balance = Number(o.balance_amount !== undefined ? o.balance_amount : Math.max(0, total - paid));

                          return (
                            <tr key={o.id}>
                              <td>
                                <strong className="order-id-highlight">
                                  {o.bill_number || o.order_no || `#ORD-${o.id}`}
                                </strong>
                              </td>
                              <td>{formatDateToDDMMYYYY(o.order_date || o.created_at)}</td>
                              <td>
                                <strong style={{ color: "#0F172A" }}>
                                  {o.patient_name || o.name || "Customer"}
                                </strong>
                                {o.patient_id && <div className="sub-table-text">{o.patient_id}</div>}
                              </td>
                              <td>{o.mobile || "-"}</td>
                              <td>
                                <strong style={{ color: "#0067B1" }}>
                                  ₹{total.toLocaleString()}
                                </strong>
                              </td>
                              <td>
                                <span style={{ color: "#16A34A", fontWeight: "700" }}>
                                  ₹{paid.toLocaleString()}
                                </span>
                              </td>
                              <td>
                                <span style={{ color: balance > 0 ? "#DC2626" : "#16A34A", fontWeight: "800" }}>
                                  ₹{balance.toLocaleString()}
                                </span>
                              </td>
                              <td>
                                <span className="payment-pill">{o.payment_mode || "Cash"}</span>
                              </td>
                              <td>
                                <span
                                  className="status-badge"
                                  style={{ backgroundColor: badge.bg, color: badge.text, borderColor: badge.border }}
                                >
                                  {normStatus}
                                </span>
                              </td>
                              <td style={{ textAlign: "center" }}>
                                <button
                                  className="view-details-action-btn"
                                  onClick={() => setSelectedBillModal(o)}
                                >
                                  <FileText size={13} />
                                  <span>View Bill</span>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* --------------------------------------------------------- */}
          {/* SUB-TAB 3: PLACED E-COMMERCE ORDERS */}
          {/* --------------------------------------------------------- */}
          {retailSubTab === "ecommerce_orders" && (
            <div className="subtab-content-container">
              {/* Filter bar for Placed E-Commerce Orders */}
              <div className="filters-strip-container">
                <div className="filters-top-row">
                  <div className="search-box-flex">
                    <Search size={16} color="#64748B" />
                    <input
                      type="text"
                      placeholder="Search order ID, supplier store code..."
                      value={retailEcommSearch}
                      onChange={(e) => setRetailEcommSearch(e.target.value)}
                    />
                    {retailEcommSearch && (
                      <button className="clear-icon-btn" onClick={() => setRetailEcommSearch("")}>
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  {/* Status Filter */}
                  <div className="select-dropdown-wrap">
                    <span className="dropdown-label">Status:</span>
                    <select
                      value={retailEcommStatusFilter}
                      onChange={(e) => setRetailEcommStatusFilter(e.target.value)}
                      className="filter-select"
                    >
                      <option value="ALL">All Statuses</option>
                      <option value="ORDERED">Ordered</option>
                      <option value="CONFIRMED">Confirmed</option>
                      <option value="SHIPPED">Shipped</option>
                      <option value="DELIVERED">Delivered</option>
                      <option value="CANCELLED">Cancelled</option>
                    </select>
                  </div>

                  <button className="export-csv-btn" onClick={exportRetailEcommOrdersCSV}>
                    <Download size={15} />
                    <span>Download All (CSV)</span>
                  </button>
                </div>

                <div className="filters-bottom-row">
                  <div className="quick-dates-group">
                    <span className="dates-label">Date Filter:</span>
                    {[
                      { id: "ALL", label: "All Time" },
                      { id: "TODAY", label: "Today" },
                      { id: "WEEK", label: "This Week" },
                      { id: "MONTH", label: "This Month" },
                      { id: "RANGE", label: "Custom Range" }
                    ].map((d) => (
                      <button
                        key={d.id}
                        className={`date-pill-btn ${retailEcommDateFilter === d.id ? "active" : ""}`}
                        onClick={() => {
                          setRetailEcommDateFilter(d.id);
                          if (d.id !== "RANGE") {
                            setRetailEcommFromDate("");
                            setRetailEcommToDate("");
                          }
                        }}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>

                  {retailEcommDateFilter === "RANGE" && (
                    <div className="custom-date-inputs">
                      <div className="date-input-field">
                        <span>From:</span>
                        <input
                          type="date"
                          value={retailEcommFromDate}
                          onChange={(e) => setRetailEcommFromDate(e.target.value)}
                        />
                      </div>
                      <div className="date-input-field">
                        <span>To:</span>
                        <input
                          type="date"
                          value={retailEcommToDate}
                          onChange={(e) => setRetailEcommToDate(e.target.value)}
                        />
                      </div>
                    </div>
                  )}

                  <span className="records-count-indicator">
                    Showing <strong>{filteredRetailEcommOrders.length}</strong> placed B2B wholesale orders
                  </span>
                </div>
              </div>

              {/* Placed E-Commerce Orders Table */}
              {retailEcommLoading ? (
                <div className="loading-box">
                  <RefreshCw className="spin-icon" size={28} color="#0067b1" />
                  <p>Loading placed wholesale orders...</p>
                </div>
              ) : filteredRetailEcommOrders.length === 0 ? (
                <div className="empty-box">
                  <ShoppingBag size={40} color="#94A3B8" />
                  <h3>No Placed E-Commerce Orders Found</h3>
                  <p>This store has not placed any wholesale orders matching your filter criteria.</p>
                </div>
              ) : (
                <div className="orders-data-card">
                  <div className="table-responsive-container">
                    <table className="custom-data-table">
                      <thead>
                        <tr>
                          <th>Order ID</th>
                          <th>Date</th>
                          <th>Supplier Store</th>
                          <th>Items Count</th>
                          <th>Total Amount</th>
                          <th>Payment Method</th>
                          <th>Status</th>
                          <th>Waybill / Partner</th>
                          <th style={{ textAlign: "center" }}>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRetailEcommOrders.map((ord) => {
                          const badge = getStatusBadge(ord.status);
                          const itemsArr = Array.isArray(ord.items) ? ord.items : [];
                          return (
                            <tr key={ord.order_id || ord.id}>
                              <td>
                                <strong className="order-id-highlight">{ord.order_id}</strong>
                              </td>
                              <td>{formatDateToDDMMYYYY(ord.created_at)}</td>
                              <td>
                                <span className="buyer-code-badge">{ord.seller_store_code || "SUPPLIER"}</span>
                              </td>
                              <td>
                                <span>{itemsArr.length} items</span>
                              </td>
                              <td>
                                <strong style={{ color: "#0067B1" }}>
                                  ₹{Number(ord.total_amount || 0).toLocaleString()}
                                </strong>
                              </td>
                              <td>
                                <span className="payment-pill">{ord.payment_method || "Cash"}</span>
                              </td>
                              <td>
                                <span
                                  className="status-badge"
                                  style={{ backgroundColor: badge.bg, color: badge.text, borderColor: badge.border }}
                                >
                                  {ord.status || "ORDERED"}
                                </span>
                              </td>
                              <td>
                                {ord.waybill_number ? (
                                  <div className="waybill-tag">
                                    <Truck size={12} />
                                    <span>{ord.waybill_number}</span>
                                  </div>
                                ) : (
                                  <span style={{ color: "#94A3B8", fontSize: "11px" }}>Not Dispatched</span>
                                )}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                <button
                                  className="view-details-action-btn"
                                  onClick={() => setSelectedOrderDetails(ord)}
                                >
                                  <Eye size={13} />
                                  <span>Details</span>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL 1: B2B E-COMMERCE ORDER DETAILS MODAL */}
      {/* ========================================================= */}
      {selectedOrderDetails && (
        <div className="modal-backdrop-overlay">
          <div className="modal-sheet-card">
            <div className="modal-sheet-header">
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <ShoppingBag size={20} color="#0067B1" />
                  <h3 className="modal-header-title">Order Details: {selectedOrderDetails.order_id}</h3>
                </div>
                <p className="modal-header-subtitle">
                  Placed on {formatDateToDDMMYYYY(selectedOrderDetails.created_at)} • Payment: {selectedOrderDetails.payment_method || "Cash"}
                </p>
              </div>
              <button className="modal-close-btn" onClick={() => setSelectedOrderDetails(null)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-scrollable-body" id="printable-ecomm-order">
              {/* Order Meta Header */}
              <div className="printable-meta-grid">
                <div>
                  <div className="meta-label">Buyer Retail Store:</div>
                  <div className="meta-val">
                    <strong>{selectedOrderDetails.store_name || selectedOrderDetails.customer_name || "Store"}</strong> ({selectedOrderDetails.store_code})
                  </div>
                  <div className="meta-sub">Phone: {selectedOrderDetails.mobile || "-"}</div>
                </div>
                <div>
                  <div className="meta-label">Supplier Store:</div>
                  <div className="meta-val">
                    <strong>{selectedOrderDetails.seller_store_code || "Direct Wholesale Hub"}</strong>
                  </div>
                  <div className="meta-sub">
                    Status: <span style={{ fontWeight: "700", color: "#0067B1" }}>{selectedOrderDetails.status || "ORDERED"}</span>
                  </div>
                </div>
                <div>
                  <div className="meta-label">Delivery Destination:</div>
                  <div className="meta-val">{selectedOrderDetails.address || "Registered Store Address"}</div>
                </div>
                <div>
                  <div className="meta-label">Shipment & Waybill:</div>
                  <div className="meta-val">
                    {selectedOrderDetails.waybill_number ? `Waybill: ${selectedOrderDetails.waybill_number}` : "Not Dispatched"}
                  </div>
                  <div className="meta-sub">Partner: {selectedOrderDetails.delivery_partner || "Standard"}</div>
                </div>
              </div>

              {/* Items Table */}
              <h4 className="modal-section-heading">Ordered Products ({Array.isArray(selectedOrderDetails.items) ? selectedOrderDetails.items.length : 1})</h4>
              <table className="modal-items-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>Brand / Category</th>
                    <th>Color / SKU</th>
                    <th>Unit Price</th>
                    <th>Qty</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(selectedOrderDetails.items) ? selectedOrderDetails.items : []).map((it, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>
                        <strong>{it.product_name || "Product"}</strong>
                      </td>
                      <td>{it.brand || "-"} / {it.category || "-"}</td>
                      <td>{it.color || it.sku || "-"}</td>
                      <td>₹{Number(it.price || 0).toLocaleString()}</td>
                      <td><strong>{it.quantity || 1}</strong></td>
                      <td><strong>₹{(Number(it.price || 0) * Number(it.quantity || 1)).toLocaleString()}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Summary Box */}
              <div className="modal-summary-box">
                <div className="summary-line">
                  <span>Grand Total Order Amount:</span>
                  <strong style={{ fontSize: "16px", color: "#0067B1" }}>
                    ₹{Number(selectedOrderDetails.total_amount || 0).toLocaleString()}
                  </strong>
                </div>
                <div className="summary-line">
                  <span>Payment Method:</span>
                  <strong>{selectedOrderDetails.payment_method || "Cash / COD"}</strong>
                </div>
                <div className="summary-line">
                  <span>Order Fulfillment Status:</span>
                  <strong>{selectedOrderDetails.status || "Ordered"}</strong>
                </div>
              </div>
            </div>

            <div className="modal-sheet-footer">
              <button className="print-modal-btn" onClick={() => handlePrintDocument("printable-ecomm-order")}>
                <Printer size={16} />
                <span>Print Order Details</span>
              </button>
              <button className="close-modal-footer-btn" onClick={() => setSelectedOrderDetails(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL 2: CLINICAL EYE EXAMINATION DOCUMENT MODAL */}
      {/* ========================================================= */}
      {selectedEyeTestModal && (
        <div className="modal-backdrop-overlay">
          <div className="modal-sheet-card">
            <div className="modal-sheet-header">
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Eye size={20} color="#0067B1" />
                  <h3 className="modal-header-title">Eye Examination & Prescription</h3>
                </div>
                <p className="modal-header-subtitle">
                  Patient: {selectedEyeTestModal.patient_name} • Date: {formatDateToDDMMYYYY(selectedEyeTestModal.exam_date || selectedEyeTestModal.created_at)}
                </p>
              </div>
              <button className="modal-close-btn" onClick={() => setSelectedEyeTestModal(null)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-scrollable-body" id="printable-eye-exam">
              <div className="print-clinic-header">
                <h2 className="header-title">{selectedRetailStore?.store_name || "Optical Eye Clinic"}</h2>
                <div className="header-sub">
                  Store Code: {selectedRetailStore?.store_code} • {selectedRetailStore?.city || selectedRetailStore?.address || ""}
                </div>
              </div>

              <div className="meta-box">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
                  <div><strong>Patient Name:</strong> {selectedEyeTestModal.patient_name}</div>
                  <div><strong>Patient ID:</strong> {selectedEyeTestModal.patient_id || "-"}</div>
                  <div><strong>Mobile:</strong> {selectedEyeTestModal.mobile_number || selectedEyeTestModal.mobile || "-"}</div>
                  <div><strong>Age / Gender:</strong> {selectedEyeTestModal.age ? `${selectedEyeTestModal.age} Yrs` : "-"} / {selectedEyeTestModal.gender || "-"}</div>
                  <div><strong>Exam Date:</strong> {formatDateToDDMMYYYY(selectedEyeTestModal.exam_date || selectedEyeTestModal.created_at)}</div>
                  <div><strong>Examiner:</strong> {selectedEyeTestModal.examiner_name || "Optometrist"}</div>
                </div>
              </div>

              <h4 style={{ fontSize: "13px", fontWeight: "800", color: "#0067b1", marginTop: 14, marginBottom: 6 }}>
                Optical Refraction Prescription
              </h4>

              <table className="modal-items-table" style={{ textAlign: "center" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>Eye</th>
                    <th style={{ textAlign: "center" }}>SPH</th>
                    <th style={{ textAlign: "center" }}>CYL</th>
                    <th style={{ textAlign: "center" }}>AXIS</th>
                    <th style={{ textAlign: "center" }}>V/A</th>
                    <th style={{ textAlign: "center" }}>ADD</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>OD (Right Eye)</strong></td>
                    <td>{selectedEyeTestModal.right_sph || "0.00"}</td>
                    <td>{selectedEyeTestModal.right_cyl || "0.00"}</td>
                    <td>{selectedEyeTestModal.right_axis ? `${selectedEyeTestModal.right_axis}°` : "-"}</td>
                    <td>{selectedEyeTestModal.right_va || "6/6"}</td>
                    <td>{selectedEyeTestModal.right_add || "-"}</td>
                  </tr>
                  <tr>
                    <td><strong>OS (Left Eye)</strong></td>
                    <td>{selectedEyeTestModal.left_sph || "0.00"}</td>
                    <td>{selectedEyeTestModal.left_cyl || "0.00"}</td>
                    <td>{selectedEyeTestModal.left_axis ? `${selectedEyeTestModal.left_axis}°` : "-"}</td>
                    <td>{selectedEyeTestModal.left_va || "6/6"}</td>
                    <td>{selectedEyeTestModal.left_add || "-"}</td>
                  </tr>
                </tbody>
              </table>

              <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={{ border: "1px solid #E2E8F0", padding: 10, borderRadius: 6 }}>
                  <div style={{ fontSize: "11px", fontWeight: "700", color: "#64748B" }}>IPD / Pupillary Distance:</div>
                  <div style={{ fontSize: "13px", fontWeight: "800", color: "#0F172A", marginTop: 2 }}>
                    {selectedEyeTestModal.pd || "-"} mm
                  </div>
                </div>

                <div style={{ border: "1px solid #E2E8F0", padding: 10, borderRadius: 6 }}>
                  <div style={{ fontSize: "11px", fontWeight: "700", color: "#64748B" }}>Lens Recommendation:</div>
                  <div style={{ fontSize: "13px", fontWeight: "800", color: "#0F172A", marginTop: 2 }}>
                    {selectedEyeTestModal.lens_recommendation || selectedEyeTestModal.lens || "Standard Rx Lens"}
                  </div>
                </div>
              </div>

              {selectedEyeTestModal.notes && (
                <div style={{ marginTop: 12, border: "1px solid #E2E8F0", padding: 10, borderRadius: 6 }}>
                  <div style={{ fontSize: "11px", fontWeight: "700", color: "#64748B" }}>Optometrist Notes & Advice:</div>
                  <div style={{ fontSize: "12px", color: "#334155", marginTop: 2 }}>{selectedEyeTestModal.notes}</div>
                </div>
              )}
            </div>

            <div className="modal-sheet-footer">
              <button className="print-modal-btn" onClick={() => handlePrintDocument("printable-eye-exam")}>
                <Printer size={16} />
                <span>Print Prescription</span>
              </button>
              <button className="close-modal-footer-btn" onClick={() => setSelectedEyeTestModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL 3: OPTICAL ORDER BILL / RECEIPT MODAL */}
      {/* ========================================================= */}
      {selectedBillModal && (
        <div className="modal-backdrop-overlay">
          <div className="modal-sheet-card">
            <div className="modal-sheet-header">
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <FileText size={20} color="#0067B1" />
                  <h3 className="modal-header-title">
                    Optical Order Bill: {selectedBillModal.bill_number || selectedBillModal.order_no || `#ORD-${selectedBillModal.id}`}
                  </h3>
                </div>
                <p className="modal-header-subtitle">
                  Customer: {selectedBillModal.patient_name || selectedBillModal.name || "Customer"} • Date: {formatDateToDDMMYYYY(selectedBillModal.order_date || selectedBillModal.created_at)}
                </p>
              </div>
              <button className="modal-close-btn" onClick={() => setSelectedBillModal(null)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-scrollable-body" id="printable-optical-bill">
              <div className="print-clinic-header">
                <h2 className="header-title">{selectedRetailStore?.store_name || "Optical Shop & Clinic"}</h2>
                <div className="header-sub">
                  Store Code: {selectedRetailStore?.store_code} • {selectedRetailStore?.city || selectedRetailStore?.address || ""}
                </div>
              </div>

              <div className="meta-box">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
                  <div><strong>Bill No:</strong> {selectedBillModal.bill_number || selectedBillModal.order_no || `#ORD-${selectedBillModal.id}`}</div>
                  <div><strong>Date:</strong> {formatDateToDDMMYYYY(selectedBillModal.order_date || selectedBillModal.created_at)}</div>
                  <div><strong>Customer Name:</strong> {selectedBillModal.patient_name || selectedBillModal.name || "Customer"}</div>
                  <div><strong>Patient ID:</strong> {selectedBillModal.patient_id || "-"}</div>
                  <div><strong>Mobile:</strong> {selectedBillModal.mobile || "-"}</div>
                  <div><strong>Delivery Date:</strong> {formatDateToDDMMYYYY(selectedBillModal.expected_delivery)}</div>
                  <div>
                    <strong>Status:</strong>{" "}
                    <span style={{ fontWeight: "700", color: "#0067B1" }}>
                      {getNormalizedOpticalStatus(selectedBillModal)}
                    </span>
                  </div>
                  <div><strong>Payment Mode:</strong> {selectedBillModal.payment_mode || "Cash"}</div>
                </div>
              </div>

              {/* Items Breakdown */}
              <h4 style={{ fontSize: "13px", fontWeight: "800", color: "#0067b1", marginTop: 14, marginBottom: 6 }}>
                Purchased Products & Frames
              </h4>

              {(() => {
                let itemsList = [];
                const rawPrescription = selectedBillModal.prescription_notes || selectedBillModal.notes || "";
                const itemsMatch = rawPrescription.match(/\[ORDER_ITEMS\]([\s\S]*?)\[\/ORDER_ITEMS\]/);
                if (itemsMatch) {
                  try {
                    const parsed = JSON.parse(itemsMatch[1]);
                    if (Array.isArray(parsed)) itemsList = parsed;
                  } catch (e) {}
                }
                if (itemsList.length === 0 && Array.isArray(selectedBillModal.items)) {
                  itemsList = selectedBillModal.items;
                }
                if (itemsList.length === 0) {
                  itemsList = [
                    {
                      product_name: selectedBillModal.frame_model || "Spectacles / Optical Order",
                      barcode: selectedBillModal.frame_barcode || "-",
                      quantity: 1,
                      unit_price: Number(selectedBillModal.total_amount || 0),
                      total_price: Number(selectedBillModal.total_amount || 0)
                    }
                  ];
                }

                return (
                  <table className="modal-items-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Product / Frame</th>
                        <th>Barcode</th>
                        <th>Qty</th>
                        <th>Unit Price</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemsList.map((it, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td><strong>{it.product_name || "Product"}</strong></td>
                          <td>{it.barcode || "-"}</td>
                          <td>{it.quantity || 1}</td>
                          <td>₹{Number(it.unit_price || it.price || 0).toLocaleString()}</td>
                          <td><strong>₹{Number(it.total_price || (Number(it.unit_price || 0) * Number(it.quantity || 1))).toLocaleString()}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}

              {/* Billing Summary Box */}
              {(() => {
                const total = Number(selectedBillModal.total_amount || 0);
                const advance = Number(selectedBillModal.advance_paid || 0);
                const balance = Number(selectedBillModal.balance_amount !== undefined ? selectedBillModal.balance_amount : Math.max(0, total - advance));

                return (
                  <div className="summary-box">
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, textAlign: "center" }}>
                      <div>
                        <div style={{ fontSize: "11px", color: "#64748B", fontWeight: "700" }}>Total Amount</div>
                        <div style={{ fontSize: "15px", color: "#0067B1", fontWeight: "800" }}>₹{total.toLocaleString()}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "#64748B", fontWeight: "700" }}>Advance Paid</div>
                        <div style={{ fontSize: "15px", color: "#16A34A", fontWeight: "800" }}>₹{advance.toLocaleString()}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "#64748B", fontWeight: "700" }}>Balance Due</div>
                        <div style={{ fontSize: "15px", color: balance > 0 ? "#DC2626" : "#16A34A", fontWeight: "800" }}>
                          ₹{balance.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "#64748B", fontWeight: "700" }}>Payment Mode</div>
                        <div style={{ fontSize: "14px", color: "#334155", fontWeight: "800" }}>{selectedBillModal.payment_mode || "Cash"}</div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="modal-sheet-footer">
              <button className="print-modal-btn" onClick={() => handlePrintDocument("printable-optical-bill")}>
                <Printer size={16} />
                <span>Print Bill / Receipt</span>
              </button>
              <button className="close-modal-footer-btn" onClick={() => setSelectedBillModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* INLINE COMPONENT STYLES */}
      {/* ========================================================= */}
      <style>{`
        .store-directory-container {
          padding: 24px;
          background: #F4F7FC;
          min-height: 100vh;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          color: #0F172A;
        }

        /* HEADER & BREADCRUMB */
        .directory-header-card {
          background: #FFFFFF;
          border-radius: 14px;
          padding: 20px 24px;
          border: 1px solid #E2E8F0;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
          margin-bottom: 20px;
        }
        .directory-header-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 16px;
        }
        .breadcrumb-nav {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #64748B;
          margin-bottom: 6px;
        }
        .breadcrumb-link {
          color: #0067B1;
          font-weight: 600;
          cursor: pointer;
        }
        .breadcrumb-link:hover {
          text-decoration: underline;
        }
        .breadcrumb-sep {
          color: #CBD5E1;
        }
        .breadcrumb-current {
          font-weight: 700;
          color: #1E293B;
        }
        .directory-page-title {
          font-size: 22px;
          font-weight: 800;
          color: #0F172A;
          margin: 0;
          letter-spacing: -0.3px;
        }
        .directory-page-subtitle {
          font-size: 13px;
          color: #64748B;
          margin: 4px 0 0 0;
        }
        .back-btn-pill {
          display: flex;
          align-items: center;
          gap: 6px;
          background: #EFF6FF;
          color: #0067B1;
          border: 1px solid #BFDBFE;
          padding: 8px 14px;
          border-radius: 8px;
          font-size: 12.5px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
        }
        .back-btn-pill:hover {
          background: #DBEAFE;
        }

        /* MAIN TABS */
        .main-tabs-wrapper {
          display: flex;
          gap: 12px;
          margin-top: 18px;
          border-top: 1px solid #F1F5F9;
          padding-top: 16px;
          flex-wrap: wrap;
        }
        .main-tab-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #F8FAFC;
          color: #475569;
          border: 1.5px solid #CBD5E1;
          padding: 10px 18px;
          border-radius: 10px;
          font-size: 13.5px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
        }
        .main-tab-btn.active {
          background: #0067B1;
          color: #FFFFFF;
          border-color: #0067B1;
          box-shadow: 0 4px 12px rgba(0, 103, 177, 0.25);
        }
        .tab-count-pill {
          background: rgba(0, 0, 0, 0.08);
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 800;
        }
        .main-tab-btn.active .tab-count-pill {
          background: rgba(255, 255, 255, 0.25);
          color: #FFFFFF;
        }

        /* KPI STRIP */
        .kpi-strip-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 14px;
          margin-bottom: 16px;
        }
        .kpi-chip {
          background: #FFFFFF;
          border: 1px solid #E2E8F0;
          border-radius: 10px;
          padding: 12px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .kpi-chip-label {
          font-size: 12px;
          color: #64748B;
          font-weight: 600;
        }
        .kpi-chip-val {
          font-size: 18px;
          font-weight: 800;
          color: #0F172A;
        }
        .text-success { color: #16A34A !important; }
        .text-warning { color: #D97706 !important; }
        .text-primary { color: #0067B1 !important; }

        /* FILTER BAR */
        .filter-card-bar {
          background: #FFFFFF;
          border-radius: 12px;
          padding: 14px 16px;
          border: 1px solid #E2E8F0;
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          align-items: center;
          margin-bottom: 18px;
        }
        .search-input-box {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #F8FAFC;
          border: 1px solid #CBD5E1;
          border-radius: 8px;
          padding: 8px 12px;
          flex: 1;
          min-width: 260px;
        }
        .search-input-box input {
          border: none;
          background: transparent;
          outline: none;
          font-size: 13px;
          width: 100%;
          color: #0F172A;
        }
        .clear-icon-btn {
          border: none;
          background: transparent;
          color: #94A3B8;
          cursor: pointer;
          display: flex;
          align-items: center;
          padding: 0;
        }
        .status-pills-row {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }
        .status-filter-pill {
          background: #F8FAFC;
          border: 1px solid #CBD5E1;
          border-radius: 6px;
          padding: 6px 12px;
          font-size: 11.5px;
          font-weight: 700;
          color: #475569;
          cursor: pointer;
        }
        .status-filter-pill.active {
          background: #0067B1;
          border-color: #0067B1;
          color: #FFFFFF;
        }

        /* STORES CARDS GRID */
        .stores-cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
          gap: 16px;
        }
        .store-overview-card {
          background: #FFFFFF;
          border: 1.5px solid #E2E8F0;
          border-radius: 14px;
          padding: 18px;
          cursor: pointer;
          transition: all 0.25s ease;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        .store-overview-card:hover {
          border-color: #0067B1;
          box-shadow: 0 8px 24px rgba(0, 103, 177, 0.1);
          transform: translateY(-2px);
        }
        .card-top-header {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }
        .store-avatar-box {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .wholesaler-avatar {
          background: #CCFBF1;
        }
        .retail-avatar {
          background: #DBEAFE;
        }
        .store-info-col {
          flex: 1;
          min-width: 0;
        }
        .store-card-name {
          font-size: 15px;
          font-weight: 800;
          color: #0F172A;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .store-code-tag {
          display: inline-block;
          font-size: 11px;
          font-weight: 700;
          color: #0067B1;
          background: #F0F9FF;
          padding: 1px 6px;
          border-radius: 4px;
          margin-top: 3px;
        }
        .status-badge {
          font-size: 10.5px;
          font-weight: 800;
          padding: 3px 8px;
          border-radius: 6px;
          border-width: 1px;
          border-style: solid;
          white-space: nowrap;
          text-transform: uppercase;
        }
        .card-divider {
          height: 1px;
          background: #F1F5F9;
          margin: 12px 0;
        }
        .store-meta-details {
          display: flex;
          flex-direction: column;
          gap: 6px;
          font-size: 12px;
          color: #475569;
        }
        .meta-line {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .card-footer-action {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 14px;
          padding-top: 10px;
          border-top: 1px dashed #E2E8F0;
        }
        .click-action-text {
          font-size: 12px;
          font-weight: 700;
          color: #0067B1;
        }

        /* HERO CARD (FOR SELECTED WHOLESALER OR RETAIL STORE) */
        .wholesaler-hero-card {
          background: linear-gradient(135deg, #0F766E 0%, #115E59 100%);
          color: #FFFFFF;
          border-radius: 14px;
          padding: 20px 24px;
          margin-bottom: 18px;
          box-shadow: 0 4px 16px rgba(15, 118, 110, 0.2);
        }
        .retail-hero-card {
          background: linear-gradient(135deg, #1E40AF 0%, #1E3A8A 100%);
          color: #FFFFFF;
          border-radius: 14px;
          padding: 20px 24px;
          margin-bottom: 18px;
          box-shadow: 0 4px 16px rgba(30, 58, 138, 0.2);
        }
        .hero-profile-row {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          flex-wrap: wrap;
        }
        .wholesaler-large-avatar, .retail-large-avatar {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .hero-profile-info {
          flex: 1;
          min-width: 0;
        }
        .hero-title-row {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        .hero-title-row h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 800;
          color: #FFFFFF;
        }
        .store-code-pill-hero {
          background: rgba(255, 255, 255, 0.2);
          padding: 3px 10px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 800;
        }
        .hero-meta-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          margin-top: 10px;
          font-size: 12.5px;
          color: #E2E8F0;
        }

        /* RETAIL SUB-TABS BAR */
        .retail-subtabs-bar {
          display: flex;
          gap: 10px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }
        .subtab-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #FFFFFF;
          border: 1.5px solid #CBD5E1;
          padding: 10px 18px;
          border-radius: 10px;
          font-size: 13.5px;
          font-weight: 700;
          color: #475569;
          cursor: pointer;
          transition: all 0.2s;
        }
        .subtab-btn.active {
          background: #0067B1;
          border-color: #0067B1;
          color: #FFFFFF;
          box-shadow: 0 4px 12px rgba(0, 103, 177, 0.25);
        }
        .subtab-count-badge {
          background: rgba(0, 0, 0, 0.08);
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 800;
        }
        .subtab-btn.active .subtab-count-badge {
          background: rgba(255, 255, 255, 0.25);
          color: #FFFFFF;
        }

        /* FILTERS STRIP CONTAINER */
        .filters-strip-container {
          background: #FFFFFF;
          border-radius: 12px;
          padding: 14px 16px;
          border: 1px solid #E2E8F0;
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 16px;
        }
        .filters-top-row {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          align-items: center;
        }
        .search-box-flex {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #F8FAFC;
          border: 1px solid #CBD5E1;
          border-radius: 8px;
          padding: 8px 12px;
          flex: 1;
          min-width: 240px;
        }
        .search-box-flex input {
          border: none;
          background: transparent;
          outline: none;
          font-size: 13px;
          width: 100%;
          color: #0F172A;
        }
        .select-dropdown-wrap {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .dropdown-label {
          font-size: 12px;
          font-weight: 700;
          color: #64748B;
        }
        .filter-select {
          border: 1px solid #CBD5E1;
          border-radius: 8px;
          padding: 8px 12px;
          background: #F8FAFC;
          font-size: 12.5px;
          color: #0F172A;
          font-weight: 600;
          outline: none;
        }
        .export-csv-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          background: #0F766E;
          color: #FFFFFF;
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 12.5px;
          font-weight: 700;
          cursor: pointer;
          transition: background 0.2s;
        }
        .export-csv-btn:hover {
          background: #115E59;
        }
        .filters-bottom-row {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          border-top: 1px solid #F1F5F9;
          padding-top: 10px;
        }
        .quick-dates-group {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }
        .dates-label {
          font-size: 12px;
          font-weight: 700;
          color: #64748B;
          margin-right: 4px;
        }
        .date-pill-btn {
          background: #F8FAFC;
          border: 1px solid #CBD5E1;
          padding: 5px 10px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 700;
          color: #475569;
          cursor: pointer;
        }
        .date-pill-btn.active {
          background: #0067B1;
          border-color: #0067B1;
          color: #FFFFFF;
        }
        .custom-date-inputs {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .date-input-field {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          font-weight: 700;
          color: #64748B;
        }
        .date-input-field input {
          border: 1px solid #CBD5E1;
          border-radius: 6px;
          padding: 4px 8px;
          font-size: 11px;
          background: #F8FAFC;
        }
        .records-count-indicator {
          font-size: 12px;
          color: #64748B;
        }

        /* DATA TABLES */
        .orders-data-card {
          background: #FFFFFF;
          border-radius: 12px;
          border: 1px solid #E2E8F0;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);
        }
        .table-responsive-container {
          overflow-x: auto;
          width: 100%;
        }
        .custom-data-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12.5px;
          min-width: 850px;
        }
        .custom-data-table th {
          background: #F8FAFC;
          color: #475569;
          font-weight: 700;
          padding: 12px 14px;
          border-bottom: 1px solid #E2E8F0;
          text-align: left;
          font-size: 11.5px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        .custom-data-table td {
          padding: 12px 14px;
          border-bottom: 1px solid #F1F5F9;
          vertical-align: middle;
        }
        .custom-data-table tr:hover td {
          background: #F8FAFC;
        }
        .order-id-highlight {
          color: #0067B1;
          font-weight: 800;
        }
        .buyer-code-badge {
          background: #EFF6FF;
          border: 1px solid #BFDBFE;
          color: #1E40AF;
          font-weight: 700;
          font-size: 11px;
          padding: 2px 6px;
          border-radius: 4px;
        }
        .patient-id-badge {
          background: #F0FDF4;
          border: 1px solid #BBF7D0;
          color: #166534;
          font-weight: 700;
          font-size: 10.5px;
          padding: 2px 6px;
          border-radius: 4px;
        }
        .sub-table-text {
          font-size: 11px;
          color: #64748B;
          margin-top: 1px;
        }
        .payment-pill {
          background: #F1F5F9;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 700;
          color: #475569;
        }
        .waybill-tag {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          color: #0F766E;
          background: #CCFBF1;
          padding: 2px 6px;
          border-radius: 4px;
          font-weight: 700;
        }
        .view-details-action-btn {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: #EFF6FF;
          border: 1px solid #93C5FD;
          color: #0067B1;
          padding: 5px 10px;
          border-radius: 6px;
          font-size: 11.5px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
        }
        .view-details-action-btn:hover {
          background: #0067B1;
          color: #FFFFFF;
        }
        .refraction-preview-pill {
          font-size: 11px;
          color: #334155;
          background: #F8FAFC;
          padding: 2px 6px;
          border-radius: 4px;
          border: 1px solid #E2E8F0;
          white-space: nowrap;
        }
        .lens-rec-text {
          max-width: 140px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: block;
          font-size: 11.5px;
          color: #475569;
        }

        /* MODAL STYLES */
        .modal-backdrop-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(15, 23, 42, 0.65);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 16px;
        }
        .modal-sheet-card {
          background: #FFFFFF;
          border-radius: 14px;
          width: 100%;
          max-width: 760px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }
        .modal-sheet-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 16px 20px;
          border-bottom: 1px solid #E2E8F0;
          background: #F8FAFC;
        }
        .modal-header-title {
          font-size: 16px;
          font-weight: 800;
          color: #0F172A;
          margin: 0;
        }
        .modal-header-subtitle {
          font-size: 12px;
          color: #64748B;
          margin: 2px 0 0 0;
        }
        .modal-close-btn {
          border: none;
          background: transparent;
          color: #64748B;
          cursor: pointer;
        }
        .modal-scrollable-body {
          padding: 20px;
          overflow-y: auto;
          flex: 1;
        }
        .printable-meta-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
          background: #F8FAFC;
          border: 1px solid #E2E8F0;
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 16px;
        }
        .meta-label {
          font-size: 11px;
          font-weight: 700;
          color: #64748B;
        }
        .meta-val {
          font-size: 12.5px;
          color: #0F172A;
          margin-top: 2px;
        }
        .meta-sub {
          font-size: 11px;
          color: #64748B;
        }
        .modal-section-heading {
          font-size: 13px;
          font-weight: 800;
          color: #0067B1;
          margin: 12px 0 6px 0;
        }
        .modal-items-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
          margin-top: 6px;
          border: 1px solid #E2E8F0;
        }
        .modal-items-table th {
          background: #F1F5F9;
          padding: 8px 10px;
          border: 1px solid #E2E8F0;
          font-size: 11px;
          font-weight: 700;
          color: #334155;
        }
        .modal-items-table td {
          padding: 8px 10px;
          border: 1px solid #E2E8F0;
        }
        .modal-summary-box {
          background: #F8FAFC;
          border: 1px solid #CBD5E1;
          border-radius: 8px;
          padding: 12px;
          margin-top: 14px;
          display: flex;
          flex-direction: column;
          gap: 6px;
          font-size: 12px;
        }
        .summary-line {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .modal-sheet-footer {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 14px 20px;
          border-top: 1px solid #E2E8F0;
          background: #F8FAFC;
        }
        .print-modal-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          background: #0067B1;
          color: #FFFFFF;
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 12.5px;
          font-weight: 700;
          cursor: pointer;
        }
        .print-modal-btn:hover {
          background: #00508B;
        }
        .close-modal-footer-btn {
          background: #E2E8F0;
          color: #334155;
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 12.5px;
          font-weight: 700;
          cursor: pointer;
        }

        /* LOADING & EMPTY STATES */
        .loading-box, .empty-box {
          background: #FFFFFF;
          border-radius: 12px;
          border: 1px solid #E2E8F0;
          padding: 48px 24px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .loading-box p {
          margin-top: 12px;
          color: #64748B;
          font-size: 13.5px;
          font-weight: 600;
        }
        .empty-box h3 {
          margin: 12px 0 4px 0;
          font-size: 16px;
          font-weight: 800;
          color: #334155;
        }
        .empty-box p {
          color: #64748B;
          font-size: 13px;
          margin: 0;
        }
        .spin-icon {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* RESPONSIVE DESIGN */
        @media (max-width: 768px) {
          .store-directory-container {
            padding: 14px;
          }
          .directory-page-title {
            font-size: 18px;
          }
          .directory-header-card {
            padding: 16px;
          }
          .hero-profile-row {
            flex-direction: column;
          }
          .filters-top-row, .filters-bottom-row {
            flex-direction: column;
            align-items: stretch;
          }
          .search-box-flex {
            min-width: 100%;
          }
          .stores-cards-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}
